import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  TRANSFORMERS,
} from '@lexical/markdown';
import type { ElementTransformer } from '@lexical/markdown';
import type { EditorState } from 'lexical';

import { $createSectionDividerNode, $isSectionDividerNode } from './section-divider-node';
import { $createSpeakerNoteNode, $isSpeakerNoteNode } from './speaker-note-node';

/**
 * Custom transformer for section dividers (`---` lines).
 * Converts `---` in markdown to SectionDividerNode and back.
 */
const SECTION_DIVIDER: ElementTransformer = {
  type: 'element',
  dependencies: [],
  regExp: /^---$/,
  replace: (parentNode) => {
    parentNode.replace($createSectionDividerNode());
  },
  export: (node) => {
    if ($isSectionDividerNode(node)) {
      return '---';
    }
    return null;
  },
};

/**
 * Custom transformer for speaker notes (`> text` lines).
 * Converts `> text` in markdown to SpeakerNoteNode and back.
 * Must appear before Lexical's built-in QUOTE transformer to intercept `> ` lines first.
 */
const SPEAKER_NOTE: ElementTransformer = {
  type: 'element',
  dependencies: [],
  regExp: /^> (.+)$/,
  replace: (parentNode, _children, match) => {
    parentNode.replace($createSpeakerNoteNode(match[1]));
  },
  export: (node) => {
    if ($isSpeakerNoteNode(node)) {
      const text = node.getText();
      return text ? `> ${text}` : '';
    }
    return null;
  },
};

/**
 * Full set of transformers: section divider and speaker note first (highest priority),
 * then Lexical's built-in markdown transformers (bold, italic, strikethrough, lists, etc.)
 * Speaker note must precede built-in QUOTE transformer to intercept `> ` lines.
 */
export const MARKDOWN_TRANSFORMERS = [SECTION_DIVIDER, SPEAKER_NOTE, ...TRANSFORMERS];

/**
 * Convert markdown content (with `---` section delimiters) to Lexical editor state.
 * Uses Lexical's built-in markdown parser plus a custom section divider transformer.
 */
export function markdownToEditorState(rawContent: string): void {
  $convertFromMarkdownString(rawContent || '', MARKDOWN_TRANSFORMERS);
}

/**
 * Serialize the current Lexical editor state to markdown.
 * SectionDividerNodes become `---` lines. Formatted text uses markdown syntax.
 */
export function editorStateToMarkdown(editorState: EditorState): string {
  let text = '';

  editorState.read(() => {
    text = $convertToMarkdownString(MARKDOWN_TRANSFORMERS);
  });

  return text;
}

// Backward-compatible aliases
export const plainTextToEditorState = markdownToEditorState;
export const editorStateToPlainText = editorStateToMarkdown;
